import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useInsightsSharedContext from "@/reports/teacher/utils/useInsightsSharedContext/useInsightsSharedContext";
import { AVAILABLE_CLASSROOM_READING_ACTIVITY_SORTING_KEYS } from "@/reports/teacher/constants";
import { StudentActivityFilters } from "@/reports/types";
import { studentsActivityMetricsQueries } from "@/reports/queries/studentsActivityMetrics";
import { API_BASE_URL } from "@/api/api.config";
import { ApiCatalog } from "@/api/catalogs/ApiCatalog";
import { wrapperCss } from "./ClassroomReadingActivity.style";
import ClassroomReadingActivityTable from "./components/ClassroomReadingActivityTable";

interface ClassroomReadingActivityProps {
  useMockData?: boolean;
}

type FilterOrder = StudentActivityFilters["activity_type_filter_order"];

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_INDEX = 1;

const ClassroomReadingActivity = (props: ClassroomReadingActivityProps) => {
  const { useMockData = false } = props;
  const { selectedFilters } = useInsightsSharedContext();
  const { classroomUniqueId, dateRange, onlyAssignedContent } = selectedFilters;
  const [filterActivityKey, setFilterActivityKey] = useState<string>();
  const [filterOrder, setFilterOrder] = useState<FilterOrder>();

  const filterOrderObj: {} = filterOrder
    ? {
        activity_type_filter: filterActivityKey,
        activity_type_filter_order: filterOrder,
      }
    : {};

  const onlyAssignedContentFilter =
    onlyAssignedContent !== null && onlyAssignedContent !== undefined
      ? {
          only_assigned_content: String(onlyAssignedContent),
        }
      : {};

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);

  const commonParams = {
    entity_id: classroomUniqueId || "",
    entity_type: "classroom" as const,
    min_date: dateRange[0],
    max_date: dateRange[1],
    ...onlyAssignedContentFilter,
  };

  const csvDownloadParams = new URLSearchParams({
    ...commonParams,
  }).toString();

  const downloadCsvUrl = `${API_BASE_URL}${ApiCatalog.studentsActivityMetricsCsv.endpoint}?${csvDownloadParams}`;

  const {
    data: responseData,
    isLoading,
    isError,
  } = useQuery(
    studentsActivityMetricsQueries.filter(
      {
        ...commonParams,
        page_size: pageSize,
        page_index: pageIndex,
        ...filterOrderObj,
        ...onlyAssignedContentFilter,
      },
      !useMockData,
    ),
  );

  const tableDataList = responseData ? responseData?.data?.data?.entities : [];
  const totalResults = responseData ? responseData?.data?.data?.total_items : 0;

  return (
    <div css={wrapperCss}>
      <ClassroomReadingActivityTable
        onControlsUpdate={(tableProps) => {
          if (tableProps.sortBy.length > 0) {
            tableProps.sortBy.forEach((sortBy) => {
              const sortKey =
                sortBy.id as keyof typeof AVAILABLE_CLASSROOM_READING_ACTIVITY_SORTING_KEYS;
              if (
                Object.keys(
                  AVAILABLE_CLASSROOM_READING_ACTIVITY_SORTING_KEYS,
                ).includes(sortKey)
              ) {
                setFilterActivityKey(
                  AVAILABLE_CLASSROOM_READING_ACTIVITY_SORTING_KEYS[sortKey],
                );
              }
              setFilterOrder(sortBy.desc ? "desc" : "asc");
            });
          } else {
            setFilterOrder(null);
          }
          setPageSize(tableProps.pageSize);
          setPageIndex(tableProps.pageIndex + 1);
        }}
        tableDataList={tableDataList}
        error={!useMockData && isError}
        loading={isLoading}
        initialState={{
          pageSize,
          totalResults,
          pageCount: Math.ceil(totalResults / pageSize),
        }}
        downloadCSVProps={{
          downloadCsvUrl,
          downloadCsvFilename: "classroom-reading-activity-data.csv",
          ariaLabel: "Reading Activity Download as CSV",
        }}
      />
    </div>
  );
};

export default ClassroomReadingActivity;
